import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitBranch } from 'lucide-react';
import PageShell from '@/components/PageShell';
import { useAppStore } from '@/store/appStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import { fetchDevices, fetchGateways, fetchListeners } from '@/features/iot/api';
import {
  getAuthoredGraph,
  getOpsStatus,
  listIngestHistory,
  listKongCatalogRoutes,
  listMcpEndpoints,
  listSystemFlows,
  listWorkspaceApis,
  putAuthoredGraph,
} from '../api';
import { extraBoardDestinations } from '../createFlow/boardOutputs';
import { composeCreatePalette } from '../createFlow/composeCreatePalette';
import {
  connectSourceToDest,
  dropSource,
  emptyGraph,
  parseAuthoredGraph,
  removeAuthoredNode,
  serializeAuthoredGraph,
} from '../createFlow/graph';
import { resolveCreateOutputs } from '../createFlow/resolveOutputs';
import type { AuthoredGraph, PaletteSource } from '../createFlow/types';
import { composeRoutingBoard } from './composeRoutingBoard';
import RoutingBoardView from './RoutingBoard';
import RouteDetailPanel from './RouteDetailPanel';
import type { HistoryTick, RouteFilter, SelectedBoardItem } from './types';

const POLL_MS = 45_000;

function sameAuthoredGraph(left: AuthoredGraph, right: AuthoredGraph): boolean {
  return left.app_id === right.app_id
    && left.updated_at === right.updated_at
    && left.nodes.length === right.nodes.length
    && left.edges.length === right.edges.length
    && left.nodes.every((node, index) => node.id === right.nodes[index]?.id)
    && left.edges.every((edge, index) => edge.id === right.edges[index]?.id);
}

const FILTERS: RouteFilter[] = ['all', 'live', 'broken', 'quiet'];

export default function DataFlowRoutingBoard() {
  const { appName } = useAppStore();
  const scopedAppId = useSelectedAppId();
  const [filter, setFilter] = useState<RouteFilter>('all');
  const [selected, setSelected] = useState<SelectedBoardItem | null>(null);

  const opsQuery = useQuery({
    queryKey: ['dataflow-ops-status', scopedAppId || 'tenant'],
    queryFn: getOpsStatus,
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 5_000,
  });

  const workspaceQuery = useQuery({
    queryKey: ['dataflow-workspace-apis', scopedAppId || 'tenant'],
    queryFn: () => listWorkspaceApis({
      app_id: scopedAppId || undefined,
      window_minutes: 1440,
    }),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 5_000,
  });

  const systemQuery = useQuery({
    queryKey: ['dataflow-system-flows', scopedAppId || 'tenant'],
    queryFn: () => listSystemFlows({ category: 'api_ingest', limit: 200 }),
    refetchInterval: POLL_MS,
    staleTime: POLL_MS - 5_000,
  });

  const model = useMemo(
    () => composeRoutingBoard({
      ops: opsQuery.data,
      workspaceApis: workspaceQuery.data,
      systemFlows: systemQuery.data,
      scopedAppId,
    }),
    [opsQuery.data, workspaceQuery.data, systemQuery.data, scopedAppId]
  );

  const selectedFeed = selected?.kind === 'feed'
    ? model.feeds.find((feed) => feed.id === selected.id) || null
    : selected?.kind === 'route'
      ? model.feeds.find((feed) => feed.id === model.routes.find((route) => route.id === selected.id)?.feedId) || null
      : null;
  const selectedRoute = selected?.kind === 'route'
    ? model.routes.find((route) => route.id === selected.id) || null
    : null;
  const selectedDest = selected?.kind === 'destination'
    ? model.destinations.find((dest) => dest.id === selected.id) || null
    : null;

  const historyQuery = useQuery({
    queryKey: ['dataflow-ingest-history', selectedFeed?.api_id || 'none', scopedAppId || 'tenant'],
    queryFn: () => listIngestHistory({
      api_id: selectedFeed!.api_id,
      app_id: scopedAppId || undefined,
      limit: 12,
    }),
    enabled: Boolean(selectedFeed?.api_id),
  });

  const devicesQuery = useQuery({
    queryKey: ['dataflow-iot-devices', scopedAppId || 'none'],
    queryFn: fetchDevices,
    enabled: Boolean(scopedAppId),
    staleTime: POLL_MS - 5_000,
  });
  const gatewaysQuery = useQuery({
    queryKey: ['dataflow-iot-gateways', scopedAppId || 'none'],
    queryFn: fetchGateways,
    enabled: Boolean(scopedAppId),
    staleTime: POLL_MS - 5_000,
  });
  const listenersQuery = useQuery({
    queryKey: ['dataflow-iot-listeners', scopedAppId || 'none'],
    queryFn: fetchListeners,
    enabled: Boolean(scopedAppId),
    staleTime: POLL_MS - 5_000,
  });
  const kongQuery = useQuery({
    queryKey: ['dataflow-kong-catalog', scopedAppId || 'none'],
    queryFn: listKongCatalogRoutes,
    enabled: Boolean(scopedAppId),
    staleTime: POLL_MS,
  });
  const mcpQuery = useQuery({
    queryKey: ['dataflow-mcp-endpoints', scopedAppId || 'none'],
    queryFn: listMcpEndpoints,
    enabled: Boolean(scopedAppId),
    staleTime: POLL_MS,
  });
  const authoredQuery = useQuery({
    queryKey: ['dataflow-authored-graph', scopedAppId || 'none'],
    queryFn: getAuthoredGraph,
    enabled: Boolean(scopedAppId),
  });

  const [graph, setGraph] = useState<AuthoredGraph>(emptyGraph(scopedAppId || ''));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirty = useRef(false);

  useEffect(() => {
    dirty.current = false;
    if (!scopedAppId) {
      setGraph((prev) => (sameAuthoredGraph(prev, emptyGraph('')) ? prev : emptyGraph('')));
      return;
    }
    const next = authoredQuery.isFetched
      ? parseAuthoredGraph(authoredQuery.data, scopedAppId)
      : emptyGraph(scopedAppId);
    setGraph((prev) => (sameAuthoredGraph(prev, next) ? prev : next));
  }, [scopedAppId]);

  useEffect(() => {
    if (!scopedAppId || dirty.current || !authoredQuery.isFetched) return;
    const next = parseAuthoredGraph(authoredQuery.data, scopedAppId);
    setGraph((prev) => (sameAuthoredGraph(prev, next) ? prev : next));
  }, [scopedAppId, authoredQuery.data, authoredQuery.isFetched]);

  const persistGraph = useCallback((next: AuthoredGraph) => {
    if (!scopedAppId) return;
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(async () => {
      setSaving(true);
      setSaveError(null);
      try {
        const saved = await putAuthoredGraph(serializeAuthoredGraph(next));
        setGraph(parseAuthoredGraph(saved, scopedAppId));
      } catch (error) {
        setSaveError(error instanceof Error ? error.message : 'Failed to persist graph');
      } finally {
        setSaving(false);
      }
    }, 250);
  }, [scopedAppId]);

  const commitGraph = useCallback((next: AuthoredGraph) => {
    dirty.current = true;
    setGraph(next);
    persistGraph(next);
  }, [persistGraph]);

  const palette = useMemo(
    () => composeCreatePalette({
      scopedAppId,
      ops: opsQuery.data,
      workspaceApis: workspaceQuery.data,
      devices: devicesQuery.data || [],
      gateways: gatewaysQuery.data || [],
      listeners: listenersQuery.data || [],
    }),
    [scopedAppId, opsQuery.data, workspaceQuery.data, devicesQuery.data, gatewaysQuery.data, listenersQuery.data]
  );

  const handleDropSource = useCallback((source: PaletteSource, position: { x: number; y: number }) => {
    if (!scopedAppId) return;
    commitGraph(dropSource(graph, source, position));
  }, [scopedAppId, graph, commitGraph]);

  const extraDests = useMemo(() => {
    const ingestOut = resolveCreateOutputs({
      sourceClass: 'ingest',
      scopedAppId,
      ops: opsQuery.data,
      pipelines: opsQuery.data?.api_pipelines?.pipelines,
      kongRoutes: kongQuery.data || [],
      mcpEndpoints: mcpQuery.data || [],
    });
    const iotOut = palette.iot.length
      ? resolveCreateOutputs({
        sourceClass: 'iot',
        scopedAppId,
        source: palette.iot[0],
        listeners: listenersQuery.data || [],
      })
      : [];
    return extraBoardDestinations(
      [...ingestOut, ...iotOut],
      model.destinations
    );
  }, [scopedAppId, opsQuery.data, kongQuery.data, mcpQuery.data, listenersQuery.data, palette.iot, model.destinations]);

  const handleConnect = useCallback((sourceNodeId: string, destId: string) => {
    const dest = extraDests.find((item) => item.id === destId);
    const destClass = dest?.class
      || (destId.startsWith('dest-store-') ? 'ingest' : undefined);
    commitGraph(connectSourceToDest(graph, sourceNodeId, destId, destClass));
  }, [graph, commitGraph, extraDests]);

  const handleRemove = useCallback((nodeId: string) => {
    commitGraph(removeAuthoredNode(graph, nodeId));
  }, [graph, commitGraph]);

  const loading = opsQuery.isLoading && workspaceQuery.isLoading;
  const loadError = opsQuery.isError && workspaceQuery.isError;

  return (
    <div className="page-dataflow h-screen flex flex-col bg-slate-50">
      <PageShell
        title="Data Flow Manager"
        subtitle="Live routing board"
        icon={GitBranch}
        iconColor="bg-blue-600"
        compact
      >
        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            <p className="text-xs text-amber-800 mb-2">
              Header service chrome is process health. Feed errors, 0 last-24h ingest, and plaintext vault counts on this page are not a Healthy claim.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              {scopedAppId ? `App: ${appName || scopedAppId}` : 'Select an app in the header to scope this board.'}
              {' · '}
              Live routes are existing ingest reads. Drag a seat source onto Platform to author a flow.
              {saving ? ' · Saving…' : ''}
            </p>

            <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="df-lane-filters">
              {FILTERS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setFilter(item)}
                  className={`text-xs px-3 py-1 rounded-full border font-medium transition ${
                    filter === item
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-indigo-300'
                  }`}
                >
                  {item === 'all' ? 'All' : item.charAt(0).toUpperCase() + item.slice(1)}
                  <span className="ml-1 opacity-70">({model.counts[item]})</span>
                </button>
              ))}
            </div>

            {loading && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-sm text-gray-500">
                Loading routing board…
              </div>
            )}
            {loadError && (
              <div className="bg-white rounded-xl border border-red-200 p-4 text-sm text-red-600 mb-4">
                Could not load ingest reads. Retrying in 45s.
              </div>
            )}
            {!loading && (
              <RoutingBoardView
                model={model}
                filter={filter}
                selected={selected}
                onSelect={setSelected}
                generatedAt={opsQuery.data?.generated_at}
                authoring={{
                  scopedAppId,
                  ingest: palette.ingest,
                  iot: palette.iot,
                  extraDests,
                  graph,
                  saving,
                  saveError,
                  onDropSource: handleDropSource,
                  onConnect: handleConnect,
                  onRemove: handleRemove,
                }}
              />
            )}
          </div>
          {selected && (
            <RouteDetailPanel
              feed={selectedFeed}
              route={selectedRoute}
              destination={selectedDest}
              ticks={(historyQuery.data || []) as HistoryTick[]}
              ticksLoading={historyQuery.isFetching}
              onClose={() => setSelected(null)}
            />
          )}
        </div>
      </PageShell>
    </div>
  );
}
